import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client } from "@/api"

/** GitHub blob base for a contact's curated docs/discovery file (by doc_slug). */
export const DOCS_CONTACTS_BASE_URL =
  "https://github.com/taitsmp/fb-clips-cf-monorepo/blob/master/docs/discovery/contacts"

// Comms data changes on a sync cadence (minutes), not per-second — a short stale
// window stops every page remount (tab switch, back-nav) from refetching.
const STALE_MS = 30_000

// ---------------------------------------------------------------------------
// Read hooks for the comms admin surface (Slice A). All endpoints live under
// /admin/comms and are backed by COMMS_DB through the backend RPC client.
// ---------------------------------------------------------------------------

export interface CommsContactRef {
  public_id: string
  display_name: string | null
  primary_email: string
  org: string | null
}

export interface OwedReplyItem {
  thread_public_id: string
  subject: string | null
  channel: "support" | "primary" | "voicemail"
  // A support thread with an inbound message to legal@ (shown as its own lens + pill).
  is_legal: boolean
  last_inbound_at: string | null
  message_count: number
  contact: CommsContactRef | null
}

export interface GoingColdItem {
  public_id: string
  display_name: string | null
  primary_email: string
  org: string | null
  doc_slug: string | null
  last_contacted_at: string | null
}

export interface CommsHealth {
  totals: { contacts: number; threads: number; messages: number }
  byClassification: { status: string; count: number }[]
  byDirection: { direction: string; count: number }[]
  lastInboundAt: string | null
  lastOutboundAt: string | null
  needsClassification: number
  // Contacts the retention sweep quietly snoozed (outreach, no reply in 21d).
  autoSnoozed: number
  syncCursor: { value: string | null; updated_at: string } | null
}

export interface TriageResponse {
  owedReply: OwedReplyItem[]
  goingCold: GoingColdItem[]
  health: CommsHealth
  coldDays: number
}

export interface CommsContactRow {
  public_id: string
  display_name: string | null
  primary_email: string
  org: string | null
  doc_slug: string | null
  doc_match_status: string
  is_relevant: boolean | null
  // The 30-day purge clock, surfaced on the contacts/bucket read so the bucket
  // view can show a real time-until-purge.
  not_relevant_at: string | null
  last_inbound_at: string | null
  last_outbound_at: string | null
  last_contacted_at: string | null
  awaiting_count: number
  // A3: the linked org (resolved server-side), so the registry's Org column can
  // link without exposing org_id. Null when unassigned.
  org_ref: OrgRef | null
}

export interface ContactsResponse {
  contacts: CommsContactRow[]
  total: number
  limit: number
  offset: number
}

export interface Commitment {
  text: string
  message_public_id: string
  sent_at: string | null
}

export interface ContactThread {
  public_id: string
  subject: string | null
  awaiting_my_reply: boolean
  // Effective owed state (nothing sent to the contact since their message); the
  // persisted flag above is stale for Gmail-synced threads, so badges use this.
  still_owed: boolean
  is_relevant: boolean | null
  // Resolved threads are handled — don't render them as "owed a reply".
  resolved_at: string | null
  message_count: number
  last_inbound_at: string | null
  last_outbound_at: string | null
}

export interface ContactDetailResponse {
  contact: {
    public_id: string
    display_name: string | null
    primary_email: string
    org: string | null
    doc_slug: string | null
    doc_match_status: string
    is_relevant: boolean | null
    not_relevant_at: string | null
    relevance_source: "human" | "agent" | null
    notes: string | null
    followup_status: FollowupStatus
    followup_reason: string | null
    last_inbound_at: string | null
    last_outbound_at: string | null
    last_contacted_at: string | null
    created_at: string
    org_ref: OrgRef | null
  }
  // A3: server-side org suggestion for an UNASSIGNED contact whose email domain
  // matches an org's primary_domain. Resolved on the backend so the picker no
  // longer fetches the whole org list just to compute it. Null when assigned or
  // no domain match.
  suggested_org: OrgRef | null
  emails: { email: string; is_primary: boolean }[]
  threads: ContactThread[]
  commitments: {
    they_asked: Commitment[]
    i_promised: Commitment[]
    suggested_followup: Commitment[]
  }
}

export interface MessageClassification {
  is_playside_relevant?: boolean
  relevance_reason?: string
  segment_signals?: string[]
  they_asked?: string[]
  i_promised?: string[]
  one_line_summary?: string
  suggested_followup?: string
}

export interface ThreadMessage {
  public_id: string
  direction: "inbound" | "outbound"
  source: string
  from_email: string
  to_emails: string[]
  cc_emails: string[]
  subject: string | null
  sent_at: string | null
  body_text: string | null
  has_attachments: boolean
  // RFC threading headers (for the Slice C reply cockpit: In-Reply-To/References).
  message_id: string
  in_reply_to: string | null
  reference_ids: string[]
  classification: MessageClassification | null
  classification_status: string
  // True when a voicemail recording exists; stream it from the audio route (the
  // storage key is never exposed).
  has_audio: boolean
}

export interface ThreadNote {
  public_id: string
  body: string
  author: string | null
  created_at: string
}

export interface ThreadDetailResponse {
  thread: {
    public_id: string
    subject: string | null
    channel: "support" | "primary" | "voicemail"
    awaiting_my_reply: boolean
    still_owed: boolean
    is_relevant: boolean | null
    not_relevant_at: string | null
    relevance_source: "human" | "agent" | null
    notes: string | null
    // "Resolved" axis — handled/done, off the owed-a-reply triage but still a real
    // thread. Null = open. Auto-cleared when a newer inbound arrives.
    resolved_at: string | null
    resolved_by: string | null
    message_count: number
    last_inbound_at: string | null
    last_outbound_at: string | null
  }
  contact: CommsContactRef | null
  messages: ThreadMessage[]
  // Running-log notes, interleaved with messages in the UI timeline.
  notes: ThreadNote[]
  // Authoritative reply recipient (true latest inbound sender, server-computed from an
  // untruncated query). The composer sends to this, not a value derived from `messages`.
  reply_recipient: string | null
  // The bare address a reply on this thread is sent from (support@ or, on a legal thread,
  // legal@), server-computed so the UI never claims the wrong sender.
  reply_from: string
}

export interface ThreadListItem {
  public_id: string
  subject: string | null
  // Channel lens: which inbound address bucket the thread arrived on.
  channel: "support" | "primary" | "voicemail"
  is_legal: boolean
  awaiting_my_reply: boolean
  still_owed: boolean
  is_relevant: boolean | null
  resolved_at: string | null
  message_count: number
  last_inbound_at: string | null
  last_outbound_at: string | null
  contact: CommsContactRef | null
}

export interface ThreadsResponse {
  threads: ThreadListItem[]
  total: number
  limit: number
  offset: number
}

export interface ActivityMessage {
  public_id: string
  direction: "inbound" | "outbound"
  source: string
  from_email: string
  to_emails: string[]
  subject: string | null
  sent_at: string | null
  has_attachments: boolean
  classification_status: string
  one_line_summary: string | null
  snippet: string | null
  thread: { public_id: string; subject: string | null } | null
  contact: CommsContactRef | null
}

export interface MessagesResponse {
  messages: ActivityMessage[]
  total: number
  limit: number
  offset: number
}

export interface WaitlistSignup {
  email: string
  source: string
  created_at: string
}

export interface WaitlistResponse {
  signups: WaitlistSignup[]
  total: number
  limit: number
  offset: number
}

export function useCropWaitlist(limit = 500, offset = 0) {
  return useQuery<WaitlistResponse>({
    queryKey: ["admin", "comms", "crop-waitlist", { limit, offset }],
    queryFn: async () => {
      const response = await client.admin.comms["crop-waitlist"].$get({
        query: { limit: limit.toString(), offset: offset.toString() },
      })
      if (!response.ok) throw new Error(`Failed to fetch Crop waitlist: ${response.status}`)
      return response.json() as Promise<WaitlistResponse>
    },
    staleTime: STALE_MS,
  })
}

export async function getCropWaitlistCsv(): Promise<Blob> {
  const response = await client.admin.comms["crop-waitlist.csv"].$get()
  if (!response.ok) throw new Error(`Failed to export Crop waitlist: ${response.status}`)
  return response.blob()
}

export function useDeleteCropWaitlistSignup() {
  const queryClient = useQueryClient()
  return useMutation<{ deleted: true }, Error, string>({
    mutationFn: async (email) => {
      const response = await client.admin.comms["crop-waitlist"].$delete({
        json: { email },
      })
      if (!response.ok) throw new Error(`Failed to delete Crop waitlist signup: ${response.status}`)
      return response.json() as Promise<{ deleted: true }>
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "comms", "crop-waitlist"] }),
  })
}

export function useCheerWaitlist(limit = 500, offset = 0) {
  return useQuery<WaitlistResponse>({
    queryKey: ["admin", "comms", "cheer-waitlist", { limit, offset }],
    queryFn: async () => {
      const response = await client.admin.comms["cheer-waitlist"].$get({
        query: { limit: limit.toString(), offset: offset.toString() },
      })
      if (!response.ok) throw new Error(`Failed to fetch Cheer waitlist: ${response.status}`)
      return response.json() as Promise<WaitlistResponse>
    },
    staleTime: STALE_MS,
  })
}

export async function getCheerWaitlistCsv(): Promise<Blob> {
  const response = await client.admin.comms["cheer-waitlist.csv"].$get()
  if (!response.ok) throw new Error(`Failed to export Cheer waitlist: ${response.status}`)
  return response.blob()
}

export function useDeleteCheerWaitlistSignup() {
  const queryClient = useQueryClient()
  return useMutation<{ deleted: true }, Error, string>({
    mutationFn: async (email) => {
      const response = await client.admin.comms["cheer-waitlist"].$delete({
        json: { email },
      })
      if (!response.ok) throw new Error(`Failed to delete Cheer waitlist signup: ${response.status}`)
      return response.json() as Promise<{ deleted: true }>
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "comms", "cheer-waitlist"] }),
  })
}

export function useCommsTriage(coldDays = 21) {
  return useQuery<TriageResponse>({
    queryKey: ["admin", "comms", "triage", { coldDays }],
    queryFn: async () => {
      const response = await client.admin.comms.triage.$get({
        query: { coldDays: coldDays.toString() },
      })
      if (!response.ok) throw new Error(`Failed to fetch triage: ${response.status}`)
      return response.json() as Promise<TriageResponse>
    },
    staleTime: STALE_MS,
  })
}

interface ContactsParams {
  limit?: number
  offset?: number
  search?: string
  relevance?: "relevant" | "irrelevant" | "unclassified"
  // The "Not relevant" bucket (quarantine). When set, the active-view default
  // is replaced by the quarantined rows.
  bucket?: "not_relevant"
  awaiting?: boolean
  sort?: "last_contacted" | "name" | "org"
}

export function useCommsContacts({
  limit = 200,
  offset = 0,
  search,
  relevance,
  bucket,
  awaiting,
  sort,
}: ContactsParams = {}) {
  return useQuery<ContactsResponse>({
    queryKey: [
      "admin",
      "comms",
      "contacts",
      { limit, offset, search, relevance, bucket, awaiting, sort },
    ],
    queryFn: async () => {
      const response = await client.admin.comms.contacts.$get({
        query: {
          limit: limit.toString(),
          offset: offset.toString(),
          ...(search ? { search } : {}),
          ...(relevance ? { relevance } : {}),
          ...(bucket ? { bucket } : {}),
          ...(awaiting ? { awaiting: "true" as const } : {}),
          ...(sort ? { sort } : {}),
        },
      })
      if (!response.ok) throw new Error(`Failed to fetch contacts: ${response.status}`)
      return response.json() as Promise<ContactsResponse>
    },
    staleTime: STALE_MS,
  })
}

export function useCommsContact(publicId: string) {
  return useQuery<ContactDetailResponse>({
    queryKey: ["admin", "comms", "contact", publicId],
    queryFn: async () => {
      const response = await client.admin.comms.contacts[":publicId"].$get({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to fetch contact: ${response.status}`)
      return response.json() as Promise<ContactDetailResponse>
    },
    enabled: !!publicId,
    staleTime: STALE_MS,
  })
}

export function useCommsThread(publicId: string) {
  return useQuery<ThreadDetailResponse>({
    queryKey: ["admin", "comms", "thread", publicId],
    queryFn: async () => {
      const response = await client.admin.comms.threads[":publicId"].$get({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to fetch thread: ${response.status}`)
      return response.json() as Promise<ThreadDetailResponse>
    },
    enabled: !!publicId,
    staleTime: STALE_MS,
  })
}

interface ThreadsParams {
  limit?: number
  offset?: number
  awaiting?: boolean
  // The "Not relevant" bucket — quarantined threads (own flag or via contact),
  // so a thread-grain-quarantined thread stays discoverable + restorable.
  // "resolved" = the archive (the default view hides resolved threads).
  bucket?: "not_relevant" | "resolved"
  // Channel lens: support@ vs the primary inbox. Omitted = all channels. "legal" = support
  // threads sent to legal@.
  channel?: "support" | "primary" | "voicemail" | "legal"
}

export function useCommsThreads({
  limit = 50,
  offset = 0,
  awaiting,
  bucket,
  channel,
}: ThreadsParams = {}) {
  return useQuery<ThreadsResponse>({
    queryKey: ["admin", "comms", "threads", { limit, offset, awaiting, bucket, channel }],
    queryFn: async () => {
      const response = await client.admin.comms.threads.$get({
        query: {
          limit: limit.toString(),
          offset: offset.toString(),
          ...(awaiting ? { awaiting: "true" as const } : {}),
          ...(bucket ? { bucket } : {}),
          ...(channel ? { channel } : {}),
        },
      })
      if (!response.ok) throw new Error(`Failed to fetch threads: ${response.status}`)
      return response.json() as Promise<ThreadsResponse>
    },
    staleTime: STALE_MS,
  })
}

// The activity-feed buckets: "not_relevant" = quarantined; "needs_classify" =
// active messages not yet classified (what the triage tile drills into).
export type MessageBucket = "not_relevant" | "needs_classify"

interface MessagesParams {
  limit?: number
  offset?: number
  direction?: "inbound" | "outbound"
  bucket?: MessageBucket
}

export function useCommsMessages({
  limit = 50,
  offset = 0,
  direction,
  bucket,
}: MessagesParams = {}) {
  return useQuery<MessagesResponse>({
    queryKey: ["admin", "comms", "messages", { limit, offset, direction, bucket }],
    queryFn: async () => {
      const response = await client.admin.comms.messages.$get({
        query: {
          limit: limit.toString(),
          offset: offset.toString(),
          ...(direction ? { direction } : {}),
          ...(bucket ? { bucket } : {}),
        },
      })
      if (!response.ok) throw new Error(`Failed to fetch messages: ${response.status}`)
      return response.json() as Promise<MessagesResponse>
    },
    staleTime: STALE_MS,
  })
}

// ---------------------------------------------------------------------------
// Write hooks (Slice A2). Relevance quarantine/restore + contact/thread
// annotations. All go through the typed RPC client; on success the caller
// invalidates the affected lists so quarantined rows disappear (and restored
// rows reappear). The relevance mutations are reversible: the inverse endpoint
// is exposed so an Undo toast can flip the boolean back.
// ---------------------------------------------------------------------------

export type FollowupStatus = "active" | "snoozed" | "no_action" | "done"

export interface RelevanceResult {
  public_id: string
  is_relevant: boolean
  not_relevant_at: string | null
}

/** Invalidate every comms list/detail query so relevance/annotation changes are reflected. */
function useInvalidateComms() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ["admin", "comms"] })
}

/** Quarantine ("not relevant") or restore a contact and all its mail. */
export function useSetContactRelevance() {
  const invalidate = useInvalidateComms()
  return useMutation<RelevanceResult, Error, { publicId: string; relevant: boolean }>({
    mutationFn: async ({ publicId, relevant }) => {
      const endpoint = relevant ? "relevant" : "not-relevant"
      const response = await client.admin.comms.contacts[":publicId"][endpoint].$post({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to update contact relevance: ${response.status}`)
      return response.json() as Promise<RelevanceResult>
    },
    onSuccess: invalidate,
  })
}

/** Quarantine ("not relevant") or restore a single thread. */
export function useSetThreadRelevance() {
  const invalidate = useInvalidateComms()
  return useMutation<RelevanceResult, Error, { publicId: string; relevant: boolean }>({
    mutationFn: async ({ publicId, relevant }) => {
      const endpoint = relevant ? "relevant" : "not-relevant"
      const response = await client.admin.comms.threads[":publicId"][endpoint].$post({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to update thread relevance: ${response.status}`)
      return response.json() as Promise<RelevanceResult>
    },
    onSuccess: invalidate,
  })
}

export interface ContactPatch {
  notes?: string | null
  followup_status?: FollowupStatus
  followup_reason?: string | null
  // A3: assign/unassign an org by public_id (null clears it).
  org_public_id?: string | null
}

export interface ContactPatchResult {
  public_id: string
  notes: string | null
  followup_status: FollowupStatus
  followup_reason: string | null
}

/** Update a contact's note and/or follow-up disposition (decoupled from relevance). */
export function usePatchContact() {
  const invalidate = useInvalidateComms()
  return useMutation<ContactPatchResult, Error, { publicId: string } & ContactPatch>({
    mutationFn: async ({ publicId, ...patch }) => {
      const response = await client.admin.comms.contacts[":publicId"].$patch({
        param: { publicId },
        json: patch,
      })
      if (!response.ok) throw new Error(`Failed to update contact: ${response.status}`)
      return response.json() as Promise<ContactPatchResult>
    },
    onSuccess: invalidate,
  })
}

export interface ThreadPatchResult {
  public_id: string
  notes: string | null
}

/** Update a thread's free-text note. */
export function usePatchThread() {
  const invalidate = useInvalidateComms()
  return useMutation<ThreadPatchResult, Error, { publicId: string; notes: string | null }>({
    mutationFn: async ({ publicId, notes }) => {
      const response = await client.admin.comms.threads[":publicId"].$patch({
        param: { publicId },
        json: { notes },
      })
      if (!response.ok) throw new Error(`Failed to update thread: ${response.status}`)
      return response.json() as Promise<ThreadPatchResult>
    },
    onSuccess: invalidate,
  })
}

export interface ResolveResult {
  public_id: string
  resolved_at: string | null
  resolved_by?: string | null
}

/** Resolve ("done") or reopen a thread — separate from relevance quarantine. */
export function useSetThreadResolved() {
  const invalidate = useInvalidateComms()
  return useMutation<ResolveResult, Error, { publicId: string; resolved: boolean }>({
    mutationFn: async ({ publicId, resolved }) => {
      const endpoint = resolved ? "resolve" : "reopen"
      const response = await client.admin.comms.threads[":publicId"][endpoint].$post({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to update thread resolved state: ${response.status}`)
      return response.json() as Promise<ResolveResult>
    },
    onSuccess: invalidate,
  })
}

export interface ResolveBatchResult {
  resolved: string[]
  resolved_at: string
}

/** Archive many threads at once (already-resolved ones are left untouched). */
export function useResolveThreadsBatch() {
  const invalidate = useInvalidateComms()
  return useMutation<ResolveBatchResult, Error, { publicIds: string[] }>({
    mutationFn: async ({ publicIds }) => {
      const response = await client.admin.comms.threads["resolve-batch"].$post({
        json: { public_ids: publicIds },
      })
      if (!response.ok) throw new Error(`Failed to archive threads: ${response.status}`)
      return response.json() as Promise<ResolveBatchResult>
    },
    onSuccess: invalidate,
  })
}

/** Append a note to a thread's running log. */
export function useAddThreadNote() {
  const invalidate = useInvalidateComms()
  return useMutation<ThreadNote, Error, { publicId: string; body: string }>({
    mutationFn: async ({ publicId, body }) => {
      const response = await client.admin.comms.threads[":publicId"].notes.$post({
        param: { publicId },
        json: { body },
      })
      if (!response.ok) throw new Error(`Failed to add note: ${response.status}`)
      return response.json() as Promise<ThreadNote>
    },
    onSuccess: invalidate,
  })
}

/** Delete one note from a thread's running log. */
export function useDeleteThreadNote() {
  const invalidate = useInvalidateComms()
  return useMutation<{ public_id: string }, Error, { publicId: string; notePublicId: string }>({
    mutationFn: async ({ publicId, notePublicId }) => {
      const response = await client.admin.comms.threads[":publicId"].notes[":notePublicId"].$delete(
        { param: { publicId, notePublicId } }
      )
      if (!response.ok) throw new Error(`Failed to delete note: ${response.status}`)
      return response.json() as Promise<{ public_id: string }>
    },
    onSuccess: invalidate,
  })
}

export interface DeleteVoicemailResult {
  public_id: string
  deleted: boolean
  thread_deleted: boolean
}

/**
 * Permanently delete one voicemail (recording + transcript), and its thread when
 * nothing else is left in it. Invalidates comms queries so thread + list refresh.
 */
export function useDeleteVoicemail() {
  const invalidate = useInvalidateComms()
  return useMutation<DeleteVoicemailResult, Error, { publicId: string }>({
    mutationFn: async ({ publicId }) => {
      const response = await client.admin.comms.messages[":publicId"].$delete({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to delete voicemail: ${response.status}`)
      return response.json() as Promise<DeleteVoicemailResult>
    },
    onSuccess: invalidate,
  })
}

export interface ReplyResult {
  thread_public_id: string
  message_public_id: string
  to: string
  // The bare address the reply was actually sent from (support@ or legal@).
  from: string
}

/**
 * Send an email reply from support@ to the thread's counterparty (Slice 10). The
 * sent message is captured back into the thread, so the console stays the system of
 * record. Invalidates comms queries so the new outbound message + cleared
 * "owed a reply" state appear immediately.
 */
export function useReplyToThread() {
  const invalidate = useInvalidateComms()
  // `to` is the recipient shown in the composer; the backend validates it against the
  // thread participants and sends to it, so a new inbound mid-compose can't redirect it.
  return useMutation<ReplyResult, Error, { publicId: string; body: string; to: string }>({
    mutationFn: async ({ publicId, body, to }) => {
      const response = await client.admin.comms.threads[":publicId"].reply.$post({
        param: { publicId },
        json: { body, to },
      })
      if (!response.ok) throw new Error(`Failed to send reply: ${response.status}`)
      return response.json() as Promise<ReplyResult>
    },
    onSuccess: invalidate,
  })
}

// On-demand AI draft (Slice 11): generate a suggested support reply for a thread on
// click and return the text to the composer. Stateless server-side (nothing stored),
// so no cache invalidation is needed; the human edits and sends via useReplyToThread.
export function useGenerateDraft() {
  return useMutation<{ draft: string; recipient: string | null }, Error, { publicId: string }>({
    mutationFn: async ({ publicId }) => {
      const response = await client.admin.comms.threads[":publicId"].draft.$post({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to generate draft: ${response.status}`)
      return response.json() as Promise<{ draft: string; recipient: string | null }>
    },
  })
}

// ---------------------------------------------------------------------------
// Organizations (Slice A3). Coverage + notes live at the org; members inherit
// the org's follow-up disposition unless they override. Org-level relevance is
// resolved at read time (marking an org not-relevant hides its members via the
// join — no member rows are written), so restore is lossless and assigning a
// contact to a quarantined org hides it immediately.
// ---------------------------------------------------------------------------

export interface OrgRef {
  public_id: string
  name: string
}

export interface CommsOrgRow {
  public_id: string
  name: string
  primary_domain: string | null
  is_relevant: boolean | null
  followup_status: FollowupStatus
  team_public_id: string | null
  member_count: number
  last_activity: string | null
}

export interface OrgsResponse {
  orgs: CommsOrgRow[]
  total: number
  limit: number
  offset: number
}

export interface OrgMember {
  public_id: string
  display_name: string | null
  primary_email: string
  is_relevant: boolean | null
  followup_status: FollowupStatus
  last_contacted_at: string | null
}

export interface OrgDetailResponse {
  org: {
    public_id: string
    name: string
    primary_domain: string | null
    notes: string | null
    is_relevant: boolean | null
    followup_status: FollowupStatus
    followup_reason: string | null
    team_public_id: string | null
    created_at: string
  }
  members: OrgMember[]
  rollups: { member_count: number; relevant_count: number; last_activity: string | null }
}

interface OrgsParams {
  limit?: number
  offset?: number
  search?: string
  // Gate the fetch — e.g. the contact picker only loads the org list when opened,
  // so a plain contact view costs zero org queries.
  enabled?: boolean
}

export function useCommsOrgs({ limit = 100, offset = 0, search, enabled = true }: OrgsParams = {}) {
  return useQuery<OrgsResponse>({
    queryKey: ["admin", "comms", "orgs", { limit, offset, search }],
    queryFn: async () => {
      const response = await client.admin.comms.orgs.$get({
        query: {
          limit: limit.toString(),
          offset: offset.toString(),
          ...(search ? { search } : {}),
        },
      })
      if (!response.ok) throw new Error(`Failed to fetch orgs: ${response.status}`)
      return response.json() as Promise<OrgsResponse>
    },
    staleTime: STALE_MS,
    enabled,
  })
}

export function useCommsOrg(publicId: string) {
  return useQuery<OrgDetailResponse>({
    queryKey: ["admin", "comms", "orgs", publicId],
    queryFn: async () => {
      const response = await client.admin.comms.orgs[":publicId"].$get({ param: { publicId } })
      if (!response.ok) throw new Error(`Failed to fetch org: ${response.status}`)
      return response.json() as Promise<OrgDetailResponse>
    },
    enabled: !!publicId,
    staleTime: STALE_MS,
  })
}

export interface CreateOrgInput {
  name: string
  primary_domain?: string | null
}

export interface CreateOrgResult {
  public_id: string
  name: string
  primary_domain: string | null
}

/** Create an org. */
export function useCreateOrg() {
  const invalidate = useInvalidateComms()
  return useMutation<CreateOrgResult, Error, CreateOrgInput>({
    mutationFn: async (input) => {
      const response = await client.admin.comms.orgs.$post({ json: input })
      if (!response.ok) throw new Error(`Failed to create org: ${response.status}`)
      return response.json() as Promise<CreateOrgResult>
    },
    onSuccess: invalidate,
  })
}

export interface OrgPatch {
  name?: string
  primary_domain?: string | null
  notes?: string | null
  followup_status?: FollowupStatus
  followup_reason?: string | null
  team_public_id?: string | null
}

/** Update org fields (name / domain / notes / disposition / product-team link). */
export function usePatchOrg() {
  const invalidate = useInvalidateComms()
  return useMutation<{ public_id: string }, Error, { publicId: string } & OrgPatch>({
    mutationFn: async ({ publicId, ...patch }) => {
      const response = await client.admin.comms.orgs[":publicId"].$patch({
        param: { publicId },
        json: patch,
      })
      if (!response.ok) throw new Error(`Failed to update org: ${response.status}`)
      return response.json() as Promise<{ public_id: string }>
    },
    onSuccess: invalidate,
  })
}

/** Mark an org not-relevant (hides its members via the read-time join) or restore. */
export function useSetOrgRelevance() {
  const invalidate = useInvalidateComms()
  return useMutation<
    { public_id: string; is_relevant: boolean; members_quarantined?: number; members_restored?: number },
    Error,
    { publicId: string; relevant: boolean }
  >({
    mutationFn: async ({ publicId, relevant }) => {
      const endpoint = relevant ? "relevant" : "not-relevant"
      const response = await client.admin.comms.orgs[":publicId"][endpoint].$post({
        param: { publicId },
      })
      if (!response.ok) throw new Error(`Failed to update org relevance: ${response.status}`)
      return response.json()
    },
    onSuccess: invalidate,
  })
}

// The retention sweep purges a quarantined row 30 days after it was marked
// not-relevant (the `not_relevant_at` clock). Keep this in sync with the
// backend cron's grace window.
export const PURGE_GRACE_DAYS = 30

/**
 * Human-readable countdown until the retention sweep may purge a quarantined
 * row. `notRelevantAt` is the ISO timestamp the row was marked not-relevant.
 * Returns a fallback string when the timestamp is unavailable (the contacts
 * list read doesn't currently carry `not_relevant_at`).
 */
export function formatPurgeCountdown(notRelevantAt: string | null | undefined): string {
  if (!notRelevantAt) return `purges ~${PURGE_GRACE_DAYS} days after quarantine`
  const marked = new Date(notRelevantAt).getTime()
  if (Number.isNaN(marked)) return `purges ~${PURGE_GRACE_DAYS} days after quarantine`
  const purgeAt = marked + PURGE_GRACE_DAYS * 24 * 60 * 60 * 1000
  const days = Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000))
  if (days <= 0) return "purges any time now"
  if (days === 1) return "purges in 1 day"
  return `purges in ${days} days`
}

// Small shared formatting helpers for the comms pages.
export function daysAgo(iso: string | null): number | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000))
}

export function formatAge(iso: string | null): string {
  const d = daysAgo(iso)
  if (d === null) return "—"
  if (d <= 0) return "today"
  if (d === 1) return "1 day ago"
  if (d < 30) return `${d} days ago`
  // Round (not floor) so 45d reads "2 months ago" rather than collapsing 30–59d
  // all into "1 month ago".
  const months = Math.max(1, Math.round(d / 30))
  return months === 1 ? "1 month ago" : `${months} months ago`
}
